/**
 * Zustand store for user authentication
 *
 * Integrates with AWS Cognito via cognitoService.
 * Persists auth state to localStorage under 'dicecapture-auth'.
 * Supports authenticated users and guest mode.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { cognitoService } from '@/services/cognitoService';
import type { AuthUser } from '@/types/auth';

const SERVICE_TAG = '[AUTH]';

interface AuthState {
  /** Currently authenticated user, or null if logged out / guest */
  user: AuthUser | null;
  /** Whether a user is currently authenticated via Cognito */
  isAuthenticated: boolean;
  /** Whether the user chose to continue as a guest */
  isGuest: boolean;
  /** Whether an auth operation is in progress */
  isLoading: boolean;
  /** Current error message (translated from Cognito error codes) */
  error: string | null;
  /** True while the persisted session is being validated on startup; false once complete */
  isHydrating: boolean;

  /**
   * Log in with email and password via Cognito.
   *
   * @param email - Email address
   * @param password - Password
   */
  login: (email: string, password: string) => Promise<void>;

  /**
   * Register a new account via Cognito, then auto-sign in.
   *
   * @param username - Display name (stored as custom:username)
   * @param email - Email address
   * @param password - Password
   */
  register: (username: string, email: string, password: string) => Promise<void>;

  /**
   * Log out the current user.
   * Clears Cognito session and store state.
   */
  logout: () => void;

  /**
   * Continue as a guest without authentication.
   * Guest users can use the app but data won't persist across devices.
   */
  continueAsGuest: () => void;

  /**
   * Clear guest status so the user can access the login/register page.
   * Does not affect other state.
   */
  clearGuest: () => void;

  /** Clear the current error message */
  clearError: () => void;

  /**
   * Initiate the forgot password flow.
   * Sends a verification code to the user's email.
   *
   * @param email - Email address of the account
   */
  forgotPassword: (email: string) => Promise<void>;

  /**
   * Confirm password reset with verification code.
   *
   * @param email - Email address of the account
   * @param code - Verification code from email
   * @param newPassword - New password to set
   */
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;

  /**
   * Change password for the currently authenticated user.
   *
   * @param oldPassword - Current password
   * @param newPassword - New password to set
   */
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
}

/**
 * Translate Cognito error codes to user-friendly messages.
 *
 * @param error - Error from Cognito SDK
 * @returns Friendly error string
 */
function translateCognitoError(error: unknown): string {
  if (!(error instanceof Error)) return 'An unexpected error occurred';

  const code = (error as { code?: string }).code ?? error.name;

  switch (code) {
    case 'NotAuthorizedException':
      return 'Incorrect email or password';
    case 'UsernameExistsException':
      return 'An account with this email already exists';
    case 'InvalidPasswordException':
      return 'Password must be at least 8 characters';
    case 'UserNotConfirmedException':
      return 'Please check your email to verify your account';
    case 'CodeMismatchException':
      return 'Invalid verification code';
    case 'LimitExceededException':
      return 'Too many attempts. Please try again later';
    case 'ExpiredCodeException':
      return 'Verification code has expired. Please request a new one';
    case 'InvalidParameterException':
      return 'Please check your input and try again';
    case 'UserNotFoundException':
      return 'No account found with this email';
    default:
      if (error.message === 'Cognito is not configured') {
        return 'Authentication service is not configured. Please contact support.';
      }
      return error.message || 'An unexpected error occurred';
  }
}

/**
 * Captured `set` function from the store creator.
 * Needed because onRehydrateStorage runs synchronously during create(),
 * when useAuthStore is still in the Temporal Dead Zone (TDZ).
 * Using useAuthStore.setState() would throw a ReferenceError.
 */
let _set: ((state: Partial<AuthState>) => void) | null = null;

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => {
      _set = set;
      return {
        user: null,
        isAuthenticated: false,
        isGuest: false,
        isLoading: false,
        error: null,
        isHydrating: true,

        login: async (email: string, password: string) => {
          console.info(`${SERVICE_TAG} Logging in`, { email });
          set({ isLoading: true, error: null });

          try {
            const result = await cognitoService.signIn(email, password);
            const user: AuthUser = {
              sub: result.sub,
              username: result.username,
              email: result.email
            };
            set({ user, isAuthenticated: true, isGuest: false, isLoading: false });
            console.info(`${SERVICE_TAG} Login successful`, { username: user.username });
          } catch (error) {
            const message = translateCognitoError(error);
            set({ isLoading: false, error: message });
            throw new Error(message);
          }
        },

        register: async (username: string, email: string, password: string) => {
          console.info(`${SERVICE_TAG} Registering new user`, { username });
          set({ isLoading: true, error: null });

          try {
            await cognitoService.signUp(username, email, password);

            // Auto-sign in after registration
            const result = await cognitoService.signIn(email, password);
            const user: AuthUser = {
              sub: result.sub,
              username: result.username,
              email: result.email
            };
            set({ user, isAuthenticated: true, isGuest: false, isLoading: false });
            console.info(`${SERVICE_TAG} Registration and auto-sign-in successful`, { username });
          } catch (error) {
            const message = translateCognitoError(error);
            set({ isLoading: false, error: message });
            throw new Error(message);
          }
        },

        logout: () => {
          console.info(`${SERVICE_TAG} Logging out`);
          cognitoService.signOut();
          set({ user: null, isAuthenticated: false, isGuest: false, error: null });
        },

        continueAsGuest: () => {
          console.info(`${SERVICE_TAG} Continuing as guest`);
          set({ user: null, isAuthenticated: false, isGuest: true, error: null });
        },

        clearGuest: () => {
          set({ isGuest: false });
        },

        clearError: () => {
          set({ error: null });
        },

        forgotPassword: async (email: string) => {
          console.info(`${SERVICE_TAG} Requesting password reset`, { email });
          set({ isLoading: true, error: null });

          try {
            await cognitoService.forgotPassword(email);
            set({ isLoading: false });
            console.info(`${SERVICE_TAG} Password reset code sent`, { email });
          } catch (error) {
            const message = translateCognitoError(error);
            set({ isLoading: false, error: message });
            throw new Error(message);
          }
        },

        changePassword: async (oldPassword: string, newPassword: string) => {
          console.info(`${SERVICE_TAG} Changing password`);
          set({ isLoading: true, error: null });

          try {
            await cognitoService.changePassword(oldPassword, newPassword);
            set({ isLoading: false });
            console.info(`${SERVICE_TAG} Password changed successfully`);
          } catch (error) {
            const message = translateCognitoError(error);
            set({ isLoading: false, error: message });
            throw new Error(message);
          }
        },

        confirmForgotPassword: async (email: string, code: string, newPassword: string) => {
          console.info(`${SERVICE_TAG} Confirming password reset`, { email });
          set({ isLoading: true, error: null });

          try {
            await cognitoService.confirmForgotPassword(email, code, newPassword);
            set({ isLoading: false });
            console.info(`${SERVICE_TAG} Password reset confirmed`, { email });
          } catch (error) {
            const message = translateCognitoError(error);
            set({ isLoading: false, error: message });
            throw new Error(message);
          }
        }
      };},
    {
      name: 'dicecapture-auth',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        isGuest: state.isGuest
      }),
      onRehydrateStorage: () => {
        console.info(`${SERVICE_TAG} Rehydrating auth state from localStorage`);
        return (state) => {
          if (!state) {
            console.info(`${SERVICE_TAG} No stored auth state — hydration complete`);
            _set!({ isHydrating: false });
            return;
          }

          // Validate stored Cognito session on rehydration
          if (state.isAuthenticated && state.user) {
            console.info(`${SERVICE_TAG} Stored session found — validating with Cognito...`);
            try {
              cognitoService.getCurrentSession().then((session) => {
                if (!session) {
                  console.info(`${SERVICE_TAG} Stored session expired — clearing auth state`);
                  _set!({
                    user: null,
                    isAuthenticated: false,
                    isGuest: false,
                    isHydrating: false
                  });
                } else {
                  console.info(`${SERVICE_TAG} Session valid — hydration complete`);
                  _set!({ isHydrating: false });
                }
              }).catch(() => {
                console.info(`${SERVICE_TAG} Session validation failed — clearing auth state`);
                _set!({
                  user: null,
                  isAuthenticated: false,
                  isGuest: false,
                  isHydrating: false
                });
              });
            } catch (err) {
              console.error(`${SERVICE_TAG} Cognito threw synchronously — clearing auth`, err);
              _set!({
                user: null,
                isAuthenticated: false,
                isGuest: false,
                isHydrating: false
              });
            }
          } else {
            console.info(`${SERVICE_TAG} Guest/unauthenticated state — hydration complete`);
            _set!({ isHydrating: false });
          }
        };
      }
    }
  )
);
